import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { initializeFirestore, memoryLocalCache, addDoc as firestoreAddDoc, setDoc as firestoreSetDoc, updateDoc as firestoreUpdateDoc, deleteDoc as firestoreDeleteDoc, onSnapshot as firestoreOnSnapshot } from "firebase/firestore";
import { getAnalytics } from "firebase/analytics";
import { getFunctions } from "firebase/functions";

const firebaseConfig = {
  apiKey: "AIzaSyCTf2SOkw6WyAwawwwlzl3UO4Bo2j4isZ8",
  authDomain: "houdashboard.firebaseapp.com",
  projectId: "houdashboard",
  storageBucket: "houdashboard.firebasestorage.app",
  messagingSenderId: "325512636868",
  appId: "1:325512636868:web:53029874e5d88ad6941c91",
  measurementId: "G-F607F2BWQG"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Delay analytics initialization to avoid auth interference
let analytics = null;
if (typeof window !== 'undefined') {
  // Initialize analytics after a short delay to avoid race conditions with auth
  setTimeout(() => {
    try {
      analytics = getAnalytics(app);
    } catch (e) {
      console.warn('Analytics init delayed:', e);
    }
  }, 2000);
}

const auth = getAuth(app);

// Initialize Firestore with memory-only cache (no IndexedDB persistence)
// This eliminates any stale writes from persisted queue
const db = initializeFirestore(app, {
  localCache: memoryLocalCache()
});

const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Note: Domain restriction removed to allow any Google account
// To re-enable for a specific domain, add:
// googleProvider.setCustomParameters({ hd: 'yourdomain.com' });

// Debug: Track the last Firestore write operation for debugging
let lastWriteOperation = null;

// Debug wrappers for Firestore write operations
export const trackedAddDoc = async (collectionRef, data) => {
  const collectionPath = collectionRef.path || collectionRef._path?.segments?.join('/') || 'unknown';
  lastWriteOperation = { op: 'addDoc', collection: collectionPath, data };
  console.log('🔶 Firestore addDoc:', collectionPath);
  try {
    return await firestoreAddDoc(collectionRef, data);
  } catch (err) {
    console.error('🔥 addDoc FAILED:', collectionPath, err.message);
    throw err;
  }
};

export const trackedSetDoc = async (docRef, data, options) => {
  const docPath = docRef.path || docRef._key?.path?.segments?.join('/') || 'unknown';
  lastWriteOperation = { op: 'setDoc', path: docPath, data };
  console.log('🔶 Firestore setDoc:', docPath);
  try {
    return await firestoreSetDoc(docRef, data, options);
  } catch (err) {
    console.error('🔥 setDoc FAILED:', docPath, err.message);
    throw err;
  }
};

export const trackedUpdateDoc = async (docRef, data) => {
  const docPath = docRef.path || docRef._key?.path?.segments?.join('/') || 'unknown';
  lastWriteOperation = { op: 'updateDoc', path: docPath, data };
  console.log('🔶 Firestore updateDoc:', docPath);
  try {
    return await firestoreUpdateDoc(docRef, data);
  } catch (err) {
    console.error('🔥 updateDoc FAILED:', docPath, err.message);
    throw err;
  }
};

export const trackedDeleteDoc = async (docRef) => {
  const docPath = docRef.path || docRef._key?.path?.segments?.join('/') || 'unknown';
  lastWriteOperation = { op: 'deleteDoc', path: docPath };
  console.log('🔶 Firestore deleteDoc:', docPath);
  try {
    return await firestoreDeleteDoc(docRef);
  } catch (err) {
    console.error('🔥 deleteDoc FAILED:', docPath, err.message);
    throw err;
  }
};

// Track all active onSnapshot subscriptions for debugging
let activeSubscriptions = [];
let subscriptionCounter = 0;

// Wrap onSnapshot to track subscriptions
export const trackedOnSnapshot = (queryOrRef, onNext, onError, onComplete) => {
  const id = ++subscriptionCounter;
  const path = queryOrRef?._query?.path?.segments?.join('/') ||
               queryOrRef?.path ||
               queryOrRef?._path?.segments?.join('/') ||
               'unknown';

  console.log(`📡 [${id}] onSnapshot STARTED:`, path);
  activeSubscriptions.push({ id, path, started: new Date() });

  const wrappedError = (err) => {
    console.error(`🔥 [${id}] onSnapshot ERROR on ${path}:`, err.code, err.message);
    if (onError) onError(err);
  };

  const unsubscribe = firestoreOnSnapshot(queryOrRef, onNext, wrappedError, onComplete);

  return () => {
    console.log(`📡 [${id}] onSnapshot STOPPED:`, path);
    activeSubscriptions = activeSubscriptions.filter(s => s.id !== id);
    unsubscribe();
  };
};

// Debug: Log all Firestore permission errors globally with full details
if (typeof window !== 'undefined') {
  // Make subscriptions available in console for debugging
  window.__firestoreSubscriptions = () => activeSubscriptions;

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason;
    if (err?.code === 'permission-denied' ||
        err?.message?.includes('permission')) {
      console.error('🔥 FIRESTORE PERMISSION ERROR');
      console.error('  Code:', err?.code);
      console.error('  Message:', err?.message);
      console.error('  Active subscriptions:', activeSubscriptions.map(s => s.path));
      console.error('  Last write op:', lastWriteOperation);
      console.trace('Full trace:');
    }
  });
}

export { app, auth, db, functions, googleProvider, analytics };
